import React, { useEffect, useState } from "react";
import Action from "../../../components/common/Action";
import DebounceSearch from "../../../components/common/DebounceSearch";
import CustomTable from "../../../components/customtable/CustomTable";
import { useDispatch, useSelector } from "react-redux";
import Breadcrumb from "../../../components/common/Breadcrumb";
import { useNavigate } from "react-router-dom";
import ConfirmationModel from "../../../components/common/ConfirmationModel";
import { deleteTips, getTipsList } from "../../../redux/nutrition-tips/slice";
import RippleLoader from "../../../components/RippleLoader/RippleLoader";
import { SortDownArrow } from "../../../assets/svg";

const NutritionTips = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchValue, setSearchValue] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [pageLimit, setPageLimit] = useState(10);
  const [pageCount, setPageCount] = useState(1);
  const [tipDelete, setTipDelete] = useState(null);
  const [sortBy, setSortBy] = useState("");
  const [sortDirection, setSortDirection] = useState("desc");
  const [isLoading, setIsLoading] = useState(false);

  // selector
  const { allTipsList, allTipsLoading } = useSelector((store) => ({
    // all RoleL ist
    allTipsList: store?.tipsData?.getTipsListStatus?.data,
    allTipsLoading: store?.tipsData?.loading,
  }));

  useEffect(() => {
    dispatch(
      getTipsList({
        search: searchValue,
        pageCount: pageCount,
        pageLimit: pageLimit,
        sort_by: sortBy,
        sort_direction: sortDirection === "asc" ? "asc" : "desc",
        limit: pageLimit,
      }),
    );
  }, [searchValue, pageCount, pageLimit, sortBy, sortDirection]);

  // Delete tip
  const deleteTipId = () => {
    setIsLoading(true);
    if (!tipDelete) return;
    dispatch(deleteTips(tipDelete)).then((res) => {
      setIsLoading(false);
      if (res.type === "deleteTips/fulfilled") {
        dispatch(
          getTipsList({
            search: searchValue,
            pageCount: pageCount,
            pageLimit: pageLimit,
            sort_by: sortBy,
            sort_direction: sortDirection === "asc" ? "asc" : "desc",
          }),
        );
      }
      setConfirmDelete(false);
    });
  };

  // Table Headers
  const ColumnHeaders = () => (
    <>
      <th className="w-16 table-th">No.</th>
      <th className="w-20 table-th">Image</th>
      <th className="table-th">
        <div
          className="flex items-center cursor-pointer gap-1 select-none"
          onClick={() => {
            setSortBy("tipName");
            setSortDirection(sortDirection === "desc" ? "asc" : "desc");
          }}
        >
          <span>Name</span>
          <span
            className={`transition-transform duration-300 ${
              sortDirection === "asc" && sortBy === "tipName"
                ? "rotate-180"
                : "rotate-0"
            }`}
          >
            <SortDownArrow />
          </span>
        </div>
      </th>

      <th className="table-th">
        <div
          className="flex items-center cursor-pointer gap-1 select-none"
          onClick={() => {
            setSortBy("details");
            setSortDirection(sortDirection === "desc" ? "asc" : "desc");
          }}
        >
          <span>Details</span>
          <span
            className={`transition-transform duration-300 ${
              sortDirection === "asc" && sortBy === "details"
                ? "rotate-180"
                : "rotate-0"
            }`}
          >
            <SortDownArrow />
          </span>
        </div>
      </th>
      <th className="w-24 table-th">Duration</th>
      <th className="w-24 table-th">Actions</th>
    </>
  );

  // Table Rows
  const DataRows = () => (
    <>
      {(allTipsList?.data?.data || [])?.map((item, index) => (
        <tr key={item.id} className=" shadow-sm text-mgrey hover:bg-navTheme/5">
          <td className="px-5 py-3">
            {(pageCount - 1) * pageLimit + index + 1}
          </td>
          <td className="px-5 py-3">
            <img
              src={item?.tipImage}
              alt="Document"
              className="w-10 h-10 object-cover"
            />
          </td>
          <td className="px-5 py-3">
            <div className="line-clamp-1">{item?.tipName || "-"}</div>
          </td>
          <td className="px-5 py-3" title={item?.details}>
            <div className="line-clamp-3 overflow-hidden">{item?.details}</div>
          </td>
          <td className="px-5 py-3">{item?.duration}</td>
          <td className="px-5 py-3">
            <Action
              optionList={
                <>
                  <li
                    className="flex gap-2 items-center px-6 py-2 bg-white hover:bg-hoverTheme transition duration-300 cursor-pointer"
                    onClick={() =>
                      navigate("/add-edit-tip", {
                        state: { nutrition: item, mode: "edit" },
                      })
                    }
                  >
                    View/Edit
                  </li>
                  <li
                    className="flex gap-2 items-center px-6 py-2 bg-white hover:bg-hoverTheme transition duration-300 cursor-pointer"
                    onClick={() => {
                      setConfirmDelete(true);
                      setTipDelete(item?._id);
                    }}
                  >
                    Delete
                  </li>
                </>
              }
            />
          </td>
        </tr>
      ))}
    </>
  );

  return (
    <>
      <div className="space-y-5">
        <div>
          <h6 className="font-Montserrat font-medium text-xl mb-3">
            Nutrition Tips
          </h6>
          <Breadcrumb
            breadCrumbParent={"Home"}
            breadCrumbParent2={"Nutrition"}
            breadCrumbActive={"Tips"}
            breadCrumbTitleKey={"/"}
            classes=""
          />
        </div>
        {/* Search and Export Section */}
        <div className="flex items-center space-x-4 w-full">
          <DebounceSearch
            className="full-width"
            search={searchValue}
            handleChange={setSearchValue}
            placeholder={"Search..."}
          />
          <button
            className="bg-themeBtn text-white flex items-center justify-center px-2 py-[7px] rounded uppercase text-base w-40 shadow-[0_4px_6px_rgba(0,0,0,0.3)]"
            onClick={() => navigate("/add-edit-Tip")}
            type="button"
          >
            + Add Tip
          </button>
        </div>

        <div className="w-full bg-white shadow rounded-lg">
          {allTipsLoading ? (
            <RippleLoader />
          ) : (
            <CustomTable
              overflowVisibleLg={true}
              columnHeaders={<ColumnHeaders />}
              dataRows={<DataRows />}
              data={allTipsList?.data}
              loading={false}
              showPagination={true}
              disableNext={!!allTipsList?.data?.length}
              disablePrev={!!allTipsList?.data?.length}
              setPaginationCurrentLimit={setPageLimit}
              paginationCurrentLimit={pageLimit}
              current={pageCount}
              total={allTipsList?.data?.pagination?.total}
              paginationData={(crPage) => setPageCount(crPage)}
              lastPage={Math.ceil(
                allTipsList?.data?.pagination?.total / pageLimit,
              )}
            />
          )}
        </div>
      </div>
      {confirmDelete && (
        <div>
          <ConfirmationModel
            confirmText={"Delete"}
            onClose={() => {
              setConfirmDelete(false);
            }}
            onConfirm={deleteTipId}
            isLoading={isLoading}
          />
        </div>
      )}
    </>
  );
};

export default NutritionTips;
